<template>
  <header
    class="site-header"
  >
    <div
      class="site-header__container"
      :class="{ 'site-header__container--raised': mobileSearchOpened }"
    >
      <!-- Left -->
      <div class="site-header__left">
        <navigation :desktop="desktop" />

        <button
          v-if="mobile.handle"
          class="site-header__menu icon-button"
          type="button"
          @click.prevent="handleMenuToggle"
        >
          <span
            class="visually-hidden"
            v-text="$string('general.site_header.menu')"
          />

          <icon-menu />
        </button>

        <button
          class="site-header__menu icon-button"
          type="button"
          @click="toggleMobileSearchOverlay"
        >
          <span
            class="visually-hidden"
            v-text="$string('general.site_header.search')"
          />

          <icon-search v-if="isSearchOpen" />
          <icon-close v-else />
        </button>
      </div>

      <!-- Logo -->
      <component
        :is="titleElement"
        class="site-header__center"
      >
        <a
          class="site-header__logo"
          :href="$variable('routes.root')"
        >
          <span
            id="site-header-title"
            class="visually-hidden"
            v-text="$variable('store.shopName')"
          />

          <brand-logo />
        </a>
      </component>

      <!-- Right -->
      <div class="site-header__right">
        <form
          class="site-header__search"
          :action="$variable('routes.search')"
          method="GET"
        >
          <icon-search
            v-if="searchStatus === 'ready'"
          />

          <button
            v-if="searchStatus === 'done'"
            type="button"
            class="site-header__search-clear text-body-xs-regular"
            @click="clearSearch"
            v-text="$string('predictiveSearch.clear')"
          />

          <input
            v-model="searchTerm"
            class="site-header__search-input"
            type="search"
            name="q"
            :placeholder="$string('general.site_header.search')"
            @input="toggleSearchOverlay"
          >
        </form>

        <a
          class="site-header__account icon-button"
          :href="$variable('routes.account.url')"
        >
          <span
            class="visually-hidden"
            v-text="$string('general.site_header.account')"
          />

          <icon-account />
        </a>

        <a
          class="site-header__cart icon-button"
          :href="$variable('routes.cart.url')"
          @click="handleCartToggle"
        >
          <span
            class="visually-hidden"
            v-text="$string('general.site_header.cart')"
          />

          <icon-cart />

          <span
            v-if="cart.item_count > 0"
            class="site-header__cart-badge critical-clear text-body-2xs-regular"
            aria-hidden="true"
            v-text="cart.item_count"
          />
        </a>
      </div>
    </div>

    <!-- Search -->
    <overlay
      key="searchShelf"
      block-class="search-shelf"
      :direction="isMobile ? 'right' : 'down'"
      namespace="searchShelf"
      :template="false"
      type="shelf"
    >
      <template #body="overlay">
        <predictive-search
          :search-term="searchTerm"
          :overlay="overlay"
          @close-search-modal="clearSearch"
          @clear-mobile-search="clearMobileSearch"
          @update-search-value="updateSearchValue"
        />
      </template>
    </overlay>

    <!-- Menu drawer -->
    <teleport
      v-if="mobile.handle"
      to="body"
    >
      <overlay
        key="menuDrawer"
        block-class="menu-drawer-overlay"
        direction="right"
        namespace="menuDrawer"
        type="drawer"
        :show-close="false"
      >
        <template #body="overlay">
          <menu-drawer
            v-if="getComponentVisible('menu-drawer')"
            v-cloak
            :handle="mobile.handle"
            :item-count="mobile.itemCount"
            :overlay="overlay"
            :secondary="mobile.secondary"
            :media="mobile.media"
            :nested-media="mobile.nestedMedia"
            :current-country="currentCountry"
            :current-language="currentLanguage"
            :localization-countries="localizationCountries"
            :flag-icons="flagIcons"
            :custom-us-country-codes="customUsCountryCodes"
            :theme-custom-country-codes="themeCustomCountryCodes"
          />

          <div
            v-if="!getComponentActive('menu-drawer')"
            class="navigation__menu-drawer"
          >
            <div
              v-for="(_, index) of [...Array(mobile.itemCount)]"
              :key="`menu-drawer-loading-${index}`"
              class="navigation__menu-drawer-link"
            >
              <span class="navigation__menu-drawer-loading" />
            </div>
          </div>
        </template>
      </overlay>
    </teleport>
  </header>
</template>

<script>

/**
 * Vue: Site header (site-header)
 * -----------------------------------------------------------------------------
 * Site header central banner containing logo, icon buttons, and menu drawer.
 * - No style import as it's included in critical stylesheet.
 *
 * @param {Object} liquid - Liquid section ID, used for theme editor events.
 * @param {Object} desktop - Desktop linklist handle and items.
 * @param {Object} mobile - Mobile linklist handle and item count.
 * @param {String} titleElement - HTML element of site logo based on page.
 * @param {String} currentCountry - Localization country ISO code.
 * @param {Array} localizationCountries - Array of localization countries.
 * @param {Object} flagIcons - Object of svg country flags.
 *
 */
import { defineAsyncComponent } from 'vue'
import { mapActions, mapGetters, mapState } from 'vuex'

import Overlay from '~global/overlay/overlay'
import Navigation from '~global/site-header-group/navigation/navigation'

import PredictiveSearch from '~async/predictive-search/predictive-search'
import BrandLogo from '~icons/general/brand-logo.svg'
import IconAccount from '~icons/general/account.svg'
import IconCart from '~icons/general/cart.svg'
import IconMenu from '~icons/general/menu.svg'
import IconClose from '~icons/directional-navigation/close.svg'
import IconSearch from '~icons/general/search.svg'

export default {
  name: 'SiteHeader',

  components: {
    BrandLogo,
    IconAccount,
    IconCart,
    IconMenu,
    IconClose,
    IconSearch,
    Navigation,
    MenuDrawer: defineAsyncComponent({
      loader: () => import(
        /* webpackChunkName: 'component.menu-drawer' */
        '~async/menu-drawer/menu-drawer'
      ),
    }),
    Overlay,
    PredictiveSearch,
  },

  props: {
    // Needed for theme editor events
    // eslint-disable-next-line vue/no-unused-properties
    liquid: {
      type: Object,
      default: () => ({}),
    },
    desktop: {
      type: Object,
      default: () => ({
        handle: 'main-menu',
        items: () => [],
      }),
    },
    mobile: {
      type: Object,
      default: () => ({
        handle: 'main-menu',
        itemCount: 5,
        secondary: () => [],
        media: () => ({}),
        nestedMedia: () => [],
      }),
    },
    titleElement: {
      type: String,
      default: 'span',
    },

    currentCountry: {
      type: String,
      default: '',
    },

    currentLanguage: {
      type: String,
      default: '',
    },

    localizationCountries: {
      type: Array,
      default: () => [],
    },
    flagIcons: {
      type: Object,
      default: () => ({}),
    },

    customUsCountryCodes: {
      type: Array,
      default: () => [],
    },

    themeCustomCountryCodes: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      searchTerm: '',
      mobileSearchOpened: false,
    }
  },

  computed: {

    /**
     * Map Vuex getters.
     */
    ...mapGetters({
      getActiveOverlay: 'overlays/getActiveOverlay',
      getComponentExists: 'components/getExists',
      searchStatus: 'search/getSearchStatus',
    }),

    /**
     * Map Vuex state.
     */
    ...mapState({
      cart: (state) => state.cart.response,
      isMobile: (state) => state.index.screen.mobile,
    }),

    isSearchOpen() {
      if (this.getActiveOverlay === 'searchShelf') {
        return false
      }

      return true
    },
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      closeOverlay: 'overlays/close',
      openOverlay: 'overlays/open',
      toggleOverlay: 'overlays/toggle',
      setSearchStatus: 'search/setSearchStatus',
    }),

    /**
     * Handle menu toggle.
     */
    handleMenuToggle() {
      this.toggleOverlay({
        component: 'menu-drawer',
        ignoreDismissed: true,
        namespace: 'menuDrawer',
      })

      this.resetSearchOverlay()
    },

    /**
     * Handle cart toggle.
     * - Prevents opening cart drawer on cart page.
     * @param {Object} event - Click event.
     */
    handleCartToggle(event) {
      if (
        this.$variable('page.type') === 'cart' ||
        !this.getComponentExists('cart-drawer')
      ) {
        return
      }

      event.preventDefault()

      this.toggleOverlay({
        component: 'cart-drawer',
        ignoreDismissed: true,
        namespace: 'cartDrawer',
      })

      this.resetSearchOverlay()
    },

    /**
     * Shopify section editor select event.
     */
    shopifySectionSelect() {
      if (
        !this.isMobile ||
        !this.getComponentExists('menu-drawer')
      ) {
        return
      }

      this.openOverlay({
        component: 'menu-drawer',
        ignoreDismissed: true,
        namespace: 'menuDrawer',
      })
    },

    /**
     * Shopify section editor deselect event.
     */
    shopifySectionDeselect() {
      this.closeOverlay('menuDrawer')
    },

    /**
     * Toggle predictive search overlay.
     */
    toggleSearchOverlay() {
      if (this.searchTerm) {
        this.openOverlay({
          ignoreDismissed: true,
          namespace: 'searchShelf',
        })

      } else {
        this.closeOverlay('searchShelf')
      }
    },

    /**
     * Toggle mobile search overlay.
     */
    toggleMobileSearchOverlay() {
      this.mobileSearchOpened = !this.mobileSearchOpened
      this.searchTerm = ''
      this.setSearchStatus('ready')

      this.toggleOverlay({
        ignoreDismissed: true,
        namespace: 'searchShelf',
      })
    },

    /**
     * Clear search input, change search status and close overlay.
     */
    clearSearch() {
      this.searchTerm = ''
      this.closeOverlay('searchShelf')
      this.setSearchStatus('ready')
    },

    /**
     * Clear search term on the mobile search overlay.
     */
    clearMobileSearch() {
      this.searchTerm = ''
    },

    /**
     * Update search value.
     * @param {String} value - search value.
     */
    updateSearchValue(value) {
      this.searchTerm = value
    },

    /**
     * Resets search overlay to ready state.
     */
    resetSearchOverlay() {
      this.mobileSearchOpened = false
      this.setSearchStatus('ready')
    },
  },
}
</script>
